import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  ok: { color: 'bg-emerald-500', text: 'text-emerald-300', label: 'Healthy' },
  warning: { color: 'bg-amber-500', text: 'text-amber-300', label: 'Watch' },
  critical: { color: 'bg-red-500', text: 'text-red-300', label: 'Near limit' },
};

export default function GoogleApiUsageCard({ adminKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsage = async () => {
    if (!adminKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/google-usage`, { params: { admin_key: adminKey } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [adminKey]); // eslint-disable-line

  const kh = data?.key_health;
  const keyValid = kh?.valid === true;
  const keyConfigured = kh?.configured;

  return (
    <Card data-testid="google-api-usage-card" className="border-purple-500/30 bg-zinc-900/60 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-purple-400" />
          <h3 className="text-base font-semibold text-white">Google Maps API Usage</h3>
        </div>
        <Button
          data-testid="google-api-usage-refresh"
          onClick={fetchUsage}
          disabled={loading}
          size="sm"
          variant="outline"
          className="border-purple-500/40 text-purple-200 hover:bg-purple-600/20"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Key health banner */}
      <div
        data-testid="google-api-key-health"
        className={`mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm ${
          !keyConfigured
            ? 'border-zinc-600 bg-zinc-800/50 text-zinc-300'
            : keyValid
            ? 'border-emerald-600/40 bg-emerald-900/20 text-emerald-200'
            : 'border-red-600/40 bg-red-900/20 text-red-200'
        }`}
      >
        {!keyConfigured ? (
          <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        ) : keyValid ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        )}
        <div>
          <p className="font-semibold">
            {!keyConfigured
              ? 'No Google API key configured'
              : keyValid
              ? 'API key is live and working'
              : 'API key is rejected by Google'}
          </p>
          {kh?.message && <p className="text-xs opacity-80">{kh.message}</p>}
        </div>
      </div>

      {error && (
        <p data-testid="google-api-usage-error" className="mb-3 text-sm text-red-400">
          {error}
        </p>
      )}

      {data && (
        <div className="space-y-3">
          <p className="text-xs text-zinc-400">
            Showing usage for <span className="font-mono text-zinc-200">{data.month}</span> · Today:{' '}
            <span className="font-mono text-zinc-200">{data.today}</span>
          </p>
          {data.apis.map((row) => {
            const style = STATUS_STYLES[row.status] || STATUS_STYLES.ok;
            const pct = Math.min(100, row.percent_used);
            return (
              <div
                key={row.api}
                data-testid={`google-api-row-${row.api}`}
                className="rounded-lg border border-zinc-700/60 bg-zinc-800/40 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">{row.label}</p>
                    <p className="text-xs text-zinc-400">
                      {row.used_this_month.toLocaleString()} / {row.free_limit.toLocaleString()} used this month
                      <span className="ml-2 text-zinc-500">({row.used_today} today)</span>
                    </p>
                  </div>
                  <Badge className={`${style.color} text-white`}>{style.label}</Badge>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-zinc-700">
                  <div className={`h-full ${style.color} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <p className={`mt-1 text-right text-xs ${style.text}`}>{row.percent_used}%</p>
              </div>
            );
          })}
          <p className="pt-2 text-xs text-zinc-500">
            Limits reset on the 1st of each month. App auto-falls-back to partner bars when limits are hit.
          </p>
        </div>
      )}
    </Card>
  );
}
